import { Checkbox, Form, Modal, Select } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { NOTE_CATEGORIES } from "../../constants/note-categories.ts"
import { type LeadNote, type NoteCategory } from "../../types/lead.types.ts"

export interface NoteFormValues {
  content: string
  category: NoteCategory
  is_pinned: boolean
}

interface NoteFormModalProps {
  /** Omitted (or null) opens the modal in create mode. */
  note?: LeadNote | null
  onClose: () => void
  onSubmit: (values: NoteFormValues) => Promise<void>
}

function NoteFormModal({ note, onClose, onSubmit }: NoteFormModalProps) {
  const { t } = useTranslation()
  const isEdit = !!note
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm<NoteFormValues>()

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setIsLoading(true)
      await onSubmit(values)
      onClose()
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      title={t(
        isEdit
          ? "leadWorkspace.cards.notes.form.editTitle"
          : "leadWorkspace.cards.notes.form.addTitle"
      )}
      open
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("leadWorkspace.cards.notes.form.save")}
      cancelText={t("leadWorkspace.cards.notes.form.cancel")}
      confirmLoading={isLoading}
      destroyOnClose
    >
      <Form<NoteFormValues>
        form={form}
        layout={"vertical"}
        initialValues={{
          content: note?.content ?? "",
          category: note?.category ?? "general",
          is_pinned: note?.is_pinned ?? false
        }}
      >
        <Form.Item
          label={t("leadWorkspace.cards.notes.form.contentLabel")}
          name={"content"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("leadWorkspace.cards.notes.form.contentRequired")
            }
          ]}
        >
          <TextArea
            rows={4}
            autoSize={{ minRows: 4, maxRows: 8 }}
            showCount
            maxLength={5000}
            placeholder={t("leadWorkspace.cards.notes.form.contentPlaceholder")}
          />
        </Form.Item>

        <Form.Item
          label={t("leadWorkspace.cards.notes.form.categoryLabel")}
          name={"category"}
        >
          <Select<NoteCategory>
            options={NOTE_CATEGORIES.map((value) => ({
              value,
              label: t(`leadWorkspace.cards.notes.category.${value}`)
            }))}
          />
        </Form.Item>

        <Form.Item name={"is_pinned"} valuePropName={"checked"}>
          <Checkbox>{t("leadWorkspace.cards.notes.form.pinLabel")}</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export { NoteFormModal }
